import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import {
  PROFILE_THEME_FILTERS,
  audienceDisplayLabel,
  catalogThemesForUser,
  formatThemeUnlockLabel,
  isThemeOwned,
  originDisplayName,
} from "../../constants/profileThemeCatalog";
import { hasCreatorAccess } from "../../utils/roleUtils";
import {
  applyProfileTheme,
  fetchThemeOwnership,
  toggleSavedTheme,
  unlockProfileTheme,
} from "../../services/profileThemeService";
import { setPageTitle } from "../../components/DocumentTitle";
import "./ThemeCatalog.css";

const TURN_MS = 280;

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function heroStyle(theme) {
  const p = theme.hero || theme.preview || {};
  const style = {};
  if (p.backgroundImage) {
    if (p.background) style.backgroundColor = p.background;
    style.backgroundImage = p.backgroundImage;
    if (p.backgroundSize) style.backgroundSize = p.backgroundSize;
    if (p.backgroundPosition) style.backgroundPosition = p.backgroundPosition;
  } else {
    style.background = p.background || "#F2EFE9";
  }
  return style;
}

function ownershipArgs(ownership) {
  return {
    unlockedIds: ownership?.unlockedProfileThemes || [],
    subscriberCount: ownership?.subscriberCount || 0,
    monthsActive: ownership?.monthsActive || 0,
    dropsUnlocked: ownership?.dropsUnlocked || 0,
  };
}

export default function ThemeCatalog() {
  const navigate = useNavigate();
  const { currentUser, userProfile, userPerks, refreshUserPerks } = useAuth();
  const isCreator = hasCreatorAccess(userProfile);
  const roleKey = isCreator ? "creator" : "fan";

  const [tab, setTab] = useState(isCreator ? "editorial" : "forYou");
  const [page, setPage] = useState(0);
  const [viewingSaved, setViewingSaved] = useState(false);
  const [savedIds, setSavedIds] = useState([]);
  const [ownership, setOwnership] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [leaveDir, setLeaveDir] = useState(null);

  const turningRef = useRef(false);
  const turnTimerRef = useRef(null);

  const reload = useCallback(async () => {
    if (!currentUser) return;
    const data = await fetchThemeOwnership(currentUser.uid);
    setOwnership(data);
    setSavedIds(data.savedThemes || []);
  }, [currentUser]);

  useEffect(() => {
    setPageTitle("100ViewsOnly — Theme Catalog");
  }, []);

  useEffect(() => {
    reload().catch((err) => setError(err.message || "Could not load themes."));
  }, [reload]);

  useEffect(() => {
    if (currentUser?.uid) refreshUserPerks(currentUser.uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  useEffect(() => {
    setTab(isCreator ? "editorial" : "forYou");
    setViewingSaved(false);
    setPage(0);
  }, [roleKey, isCreator]);

  useEffect(() => () => {
    if (turnTimerRef.current) window.clearTimeout(turnTimerRef.current);
  }, []);

  const visibleThemes = useMemo(
    () => catalogThemesForUser(userProfile),
    [userProfile]
  );

  const items = useMemo(() => {
    if (viewingSaved) {
      const saved = new Set(savedIds);
      return visibleThemes.filter((t) => saved.has(t.id));
    }
    return visibleThemes.filter((t) => t.origin === tab);
  }, [viewingSaved, savedIds, visibleThemes, tab]);

  const savedVisibleCount = useMemo(() => {
    const saved = new Set(savedIds);
    return visibleThemes.filter((t) => saved.has(t.id)).length;
  }, [savedIds, visibleThemes]);

  useEffect(() => {
    const max = Math.max(0, Math.ceil(items.length / 2) - 1);
    setPage((p) => Math.min(p, max));
  }, [items.length]);

  const totalSpreads = Math.max(1, Math.ceil(items.length / 2));
  const safePage = Math.min(page, totalSpreads - 1);
  const leftTheme = items[safePage * 2];
  const rightTheme = items[safePage * 2 + 1];

  if (!currentUser) {
    return <Navigate to="/welcome?auth=signin" replace />;
  }

  const credits = userPerks?.credits ?? ownership?.credits ?? 0;
  const appliedId = ownership?.profileTheme;

  const showToast = (msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2800);
  };

  const closeCatalog = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/perks");
  };

  const handleSelectTab = (nextTab) => {
    setTab(nextTab);
    setViewingSaved(false);
    setPage(0);
  };

  const handleToggleSavedView = () => {
    setViewingSaved((on) => !on);
    setPage(0);
  };

  const handleToggleSave = async (theme, event) => {
    event.stopPropagation();
    if (!currentUser?.uid || busyId) return;
    const currentlySaved = savedIds.includes(theme.id);
    setSavedIds((prev) => (
      currentlySaved ? prev.filter((id) => id !== theme.id) : [...prev, theme.id]
    ));
    try {
      await toggleSavedTheme(currentUser.uid, theme.id, currentlySaved);
    } catch (err) {
      setSavedIds((prev) => (
        currentlySaved
          ? (prev.includes(theme.id) ? prev : [...prev, theme.id])
          : prev.filter((id) => id !== theme.id)
      ));
      setError(err?.message || "Could not save theme.");
    }
  };

  const handleCardClick = async (theme) => {
    if (!ownership || busyId || !theme) return;
    setError("");
    const owned = isThemeOwned(theme, ownershipArgs(ownership));

    setBusyId(theme.id);
    try {
      if (!owned) {
        if (theme.unlockType === "credits") {
          if (credits < (theme.creditsCost || 0)) {
            setError(`Need ${Number(theme.creditsCost).toLocaleString("en-US")} credits to unlock ${theme.name}.`);
            return;
          }
          await unlockProfileTheme(theme.id);
          await applyProfileTheme(theme.id);
          showToast(`${theme.name} unlocked & applied`);
        } else if (theme.unlockType === "achievement") {
          await unlockProfileTheme(theme.id);
          await applyProfileTheme(theme.id);
          showToast(`${theme.name} unlocked & applied`);
        } else {
          await applyProfileTheme(theme.id);
          showToast(`${theme.name} applied`);
        }
      } else if (appliedId === theme.id) {
        showToast("Already applied");
      } else {
        await applyProfileTheme(theme.id);
        showToast(`${theme.name} applied`);
      }
      await reload();
      if (currentUser?.uid) await refreshUserPerks(currentUser.uid);
    } catch (err) {
      setError(err?.message || err?.code || "Something went wrong.");
    } finally {
      setBusyId(null);
    }
  };

  const turnPage = (dir) => {
    if (turningRef.current) return;
    const next = safePage + dir;
    if (next < 0 || next >= totalSpreads) return;
    if (prefersReducedMotion()) {
      setPage(next);
      return;
    }
    turningRef.current = true;
    setLeaveDir(dir > 0 ? "fwd" : "back");
    turnTimerRef.current = window.setTimeout(() => {
      setPage(next);
      setLeaveDir(null);
      turningRef.current = false;
    }, TURN_MS);
  };

  const leaveClass = leaveDir
    ? `fade-panel leaving-${leaveDir}`
    : "fade-panel";

  const pageIndicator = items.length
    ? `Page ${safePage * 2 + 1}${rightTheme ? `\u2013${safePage * 2 + 2}` : ""} of ${items.length}`
    : "Nothing here yet";

  const renderThemePage = (theme, index) => {
    if (!theme) return <div />;
    const owned = ownership
      ? isThemeOwned(theme, ownershipArgs(ownership))
      : theme.unlockType === "free";
    const applied = appliedId === theme.id;
    const saved = savedIds.includes(theme.id);
    let unlockClass = theme.unlockType;
    let unlockText = formatThemeUnlockLabel(theme);
    if (applied) {
      unlockClass = "owned";
      unlockText = "Applied";
    } else if (owned) {
      unlockClass = "owned";
      if (theme.unlockType !== "free") unlockText = "Owned";
    }
    if (busyId === theme.id) unlockText = "Working…";

    return (
      <div
        className="theme-page"
        role="button"
        tabIndex={0}
        onClick={() => handleCardClick(theme)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleCardClick(theme);
          }
        }}
        aria-label={`${theme.name}${applied ? ", applied" : owned ? ", owned" : ", locked"}`}
      >
        <div className="tp-hero" style={heroStyle(theme)}>
          <button
            type="button"
            className={`tp-save-btn${saved ? " saved" : ""}`}
            aria-label={saved ? `Unsave ${theme.name}` : `Save ${theme.name}`}
            onClick={(e) => handleToggleSave(theme, e)}
          >
            {saved ? "♥" : "♡"}
          </button>
          {owned ? (
            <div className="tp-hero-owned">✓</div>
          ) : (
            <div className="tp-hero-lock">🔒</div>
          )}
        </div>
        <div className="tp-origin">
          {originDisplayName(theme.origin)} · {audienceDisplayLabel(theme.audience)}
        </div>
        <div className="tp-name">
          {theme.name}
          {theme.isNew ? <span className="tp-new">New</span> : null}
        </div>
        <div className="tp-desc">{theme.desc}</div>
        <div className="tp-foot-row">
          <span className={`tp-unlock ${unlockClass}`}>{unlockText}</span>
          <span className="tp-pagenum">{index + 1}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="tc-page">
      <div className="mag">
        <button type="button" className="mag-close" onClick={closeCatalog} aria-label="Close catalog">
          ✕
        </button>

        <div className="masthead">
          <span className="mh-issue">Issue No. 01</span>
          <span className="mh-aside">swap it anytime, no penalty</span>
          <div className="mh-eyebrow">100ViewsOnly Presents</div>
          <div className="mh-wordmark">Theme <em>Catalog</em></div>
          <div className="mh-tagline">a new look for your profile, this season</div>
          <div className="mh-balance">
            Your balance <strong>{Number(credits).toLocaleString("en-US")} credits</strong>
          </div>
        </div>

        <div className="mag-controls">
          <span className="controls-aside">new ones drop often</span>
          <select
            className="tab-select"
            value={tab}
            aria-label="Theme category"
            onChange={(e) => handleSelectTab(e.target.value)}
          >
            {PROFILE_THEME_FILTERS.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
          <button
            type="button"
            className={`saved-btn${viewingSaved ? " active" : ""}`}
            onClick={handleToggleSavedView}
          >
            <span className="heart-icon">♡</span> Saved{" "}
            <span className="saved-count">({savedVisibleCount})</span>
          </button>
        </div>

        {error && <p className="tc-error">{error}</p>}

        <div className="spread-stage">
          <div className="spread-grid">
            <div className={`spread-col fade-left ${leaveClass}`}>
              {renderThemePage(leftTheme, safePage * 2)}
            </div>
            <div className="spread-divider" />
            <div className={`spread-col ${leaveClass}`} id="col-right">
              {renderThemePage(rightTheme, safePage * 2 + 1)}
            </div>
          </div>
        </div>

        <div className="mag-foot">
          <span className="foot-aside">take your time, flip through</span>
          <button
            type="button"
            className="foot-arrow"
            onClick={() => turnPage(-1)}
            disabled={safePage <= 0}
            aria-label="Previous page"
          >
            ‹
          </button>
          <div className="foot-page">{pageIndicator}</div>
          <button
            type="button"
            className="foot-arrow"
            onClick={() => turnPage(1)}
            disabled={safePage >= totalSpreads - 1}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      </div>

      {toast && <div className="tc-toast" role="status">{toast}</div>}
    </div>
  );
}
